import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, BookOpen, Heart, Eye } from "lucide-react";
import { API, COVER_IMAGE } from "../lib/constants";
import WitnessStandTabs from "../components/WitnessStandTabs";

const GENRES = ["All", "Mystery", "Thriller", "Crime", "Drama", "Slice-of-Life", "Memoir", "Comedy", "Horror", "Romance", "Other"];

export default function WitnessStandLibrary() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState(params.get("genre") || "All");
  const [search, setSearch] = useState(params.get("q") || "");
  const [sort, setSort] = useState(params.get("sort") || "recent");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const q = new URLSearchParams();
      if (genre && genre !== "All") q.set("genre", genre);
      if (search) q.set("search", search);
      q.set("sort", sort);
      try {
        const r = await fetch(`${API}/witness-stand/stories?${q}`);
        if (!r.ok) {
          if (!cancelled) { setStories([]); setTotal(0); }
          return;
        }
        const d = await r.json();
        if (!cancelled) {
          setStories(d.stories || []);
          setTotal(d.total || 0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [genre, search, sort]);

  const updateParams = (next) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
    setParams(p);
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="witness-stand-library">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <p className="font-mono text-red-500 text-xs tracking-[0.3em] mb-2">LISTENER LIBRARY</p>
          <h1 className="font-cinzel text-3xl md:text-4xl font-bold mb-2">THE WITNESS STAND</h1>
          <p className="text-gray-400 text-sm max-w-xl mx-auto">Stories published by listeners. Free to read. Submit your own and be featured.</p>
        </div>

        {/* Tabs */}
        <WitnessStandTabs />

        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-mono text-gray-500" data-testid="library-total">{total} {total === 1 ? "STORY" : "STORIES"}</span>
        </div>

        {/* Search + filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); updateParams({ q: e.target.value }); }}
              placeholder="Search by title, author, or subtitle…"
              className="w-full pl-9 pr-4 py-2.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500/50"
              data-testid="library-search"
            />
          </div>
          <select
            value={genre}
            onChange={(e) => { setGenre(e.target.value); updateParams({ genre: e.target.value === "All" ? null : e.target.value }); }}
            className="px-3 py-2.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500/50"
            data-testid="library-genre"
          >
            {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); updateParams({ sort: e.target.value }); }}
            className="px-3 py-2.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500/50"
            data-testid="library-sort"
          >
            <option value="recent">Most Recent</option>
            <option value="popular">Most Liked</option>
          </select>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-12 font-mono text-xs">LOADING…</p>
        ) : stories.length === 0 ? (
          <div className="text-center py-16 border border-white/5 rounded-xl bg-[#0a0a0a]" data-testid="library-empty">
            <BookOpen size={32} className="text-red-500/40 mx-auto mb-3" />
            <p className="text-gray-400 text-sm mb-4">No stories yet — be the first to publish.</p>
            <button onClick={() => navigate("/witness-stand/write")} className="px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold text-white" data-testid="library-empty-cta">
              Publish Your Story
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
            {stories.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/witness-stand/story/${s.id}`)}
                className="text-left bg-[#0a0a0a] border border-white/10 hover:border-red-500/40 rounded-xl overflow-hidden group transition-colors"
                data-testid={`library-card-${s.id}`}
              >
                <div className="relative aspect-[16/10] bg-cover bg-center" style={{ backgroundImage: `url(${s.cover_url || COVER_IMAGE})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-red-600/90 text-[10px] font-mono text-white">{s.genre.toUpperCase()}</div>
                </div>
                <div className="p-4">
                  <p className="font-cinzel text-base font-bold text-white mb-1 line-clamp-2 group-hover:text-red-400 transition-colors">{s.title}</p>
                  {s.subtitle && <p className="text-xs text-gray-400 mb-2 line-clamp-2">{s.subtitle}</p>}
                  <div className="flex items-center justify-between text-[10px] font-mono text-gray-500">
                    <span>by {s.author_name}</span>
                    <span className="flex items-center gap-2">
                      <span className="flex items-center gap-1"><Eye size={10}/>{s.view_count || 0}</span>
                      <span className="flex items-center gap-1"><Heart size={10}/>{s.likes_count || 0}</span>
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
